import '@spotfli/ui/base.css';
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import useAutoLogout from "./hooks/useAutoLogout";
import UserProtectedRoute from "./components/protected-routes/user-protected-route";

// Common Components
import ComingSoon from "./components/common/ComingSoon";
import Home from "./components/home/Home";
import Rent from "./components/home/listings/rent/Rent";
import SearchResults from './components/pages/SearchResults';
import PropertyDetails from "./components/propertyDetails/PropertyDetails";
import HotelDetailsPage from "./components/home/listings/hotel/HotelDetailsPage";
import ChatApp from "./components/chat-system/ChatApp";
import NetworkStatusPopup from "./components/common/NetworkStatusPopup";

// Login Components
import Login from "./components/login/Login";
import ForgotPassword from "./components/register/propertySeeker/ForgotPassword";
import ResetPassword from "./components/register/propertySeeker/ResetPassword";

// Register Components
import Register from "./components/register/propertySeeker/Register";
import AccountType from "./components/register/AccountType";
import RegisterServiceProvider from "./components/register/serviceProvider/RegisterServiceProvider";
import RegisisterPropertyOwner from "./components/register/propertyOwner/RegisisterPropertyOwner";
import AddServiceInfo from "./components/register/serviceProvider/AddServiceInfo";
import ExperienceLevel from "./components/register/serviceProvider/ExperienceLevel";

// Email Verification & Onboarding
import UserEmailVerification from "./components/email-verification/UserEmailVerification";
import OnboardingIntent from "./components/onboarding/OnboardingIntent";

// User Components
import BuyProperty from "./components/views/user/buy-prop";
import RentView from './components/views/user/rent-view';
import AccountBillingView from "./components/views/user/account-billing";
import AccountManagement from './components/account-billing/account/AccountManagement';
import UpdateAccountView from "./components/views/user/update-account";
import HelpView from "./components/views/user/help-view";
import VacationView from "./components/views/user/vacation-plan";
import LandView from "./components/views/user/land-view";
import ServiceProviderView from "./components/views/user/service-provider"; // ✅ Now used
import SingleServiceView from "./components/views/user/single-provider";
import HotelsView from "./components/views/user/hotels-view";
import ColivingView from "./components/views/user/coliving-view";
import Bookings from './components/views/user/Bookings';


// Guest House Component & Layout
import GuestHouse from './components/home/listings/hotel/GuestHouse';


// import MoreInfo from "./components/views/property/add-property/more-info/more-info";

// Service Provider Components & Layout
import About from "./components/home/about/About";
import TermsAndConditions from "./components/terms-conditions/TermsAndConditions";
import ServiceSelector from "./components/home/ServiceSelectore";

// Role dashboards (formerly separate apps) load on demand
import Loader from "./components/common/Loader";
const PropertyOwnerRoutes = lazy(() => import("./features/property-owner/PropertyOwnerRoutes"));
const ServiceProviderRoutes = lazy(() => import("./features/service-provider/ServiceProviderRoutes"));
const HotelRoutes = lazy(() => import("./features/hotel/HotelRoutes"));

// Auto logout wrapper component
const AutoLogoutWrapper = ({ children }) => {
  useAutoLogout(60); // Auto logout after 1 hour of inactivity
  return children;
};

function App() {
  return (
    <div>
      <NetworkStatusPopup />
      <BrowserRouter>
        <AutoLogoutWrapper>
          <Routes>
            {/* Public Routes */}
            <Route path='/' element={<><ServiceSelector /></>} />
            <Route path="/terms-conditions" element={<TermsAndConditions />} />
            <Route path="/search-results" element={<><Home /><SearchResults /></>} />
            <Route path='/hotels' element={<HotelsView />} />
            <Route path="/guest-house" element={<><Home /><GuestHouse /></>} />
            <Route path='/rent' element={<RentView />} />
            <Route path='/Land' element={<LandView />} />
            <Route path='/land' element={<LandView />} />
            <Route path='/coliving' element={<ComingSoon />} />
            <Route path='/comingSoon' element={<ComingSoon />} />
            <Route path='/chatapp' element={<ChatApp />} />
            <Route path='/home' element={<Home />} />
            <Route path='/about-us' element={<About />} />
            <Route path="/buy" element={<><BuyProperty /></>} />
            <Route path='/serviceProvider' element={<ServiceProviderView />} />


            {/* Login Routes */}
            <Route path='/login' element={<Login />} />
            <Route path='/forgot-password' element={<ForgotPassword />} />
            <Route path='/reset-password/:token' element={<ResetPassword />} />

            {/* Register Routes */}
            <Route path="/register" element={<Register />} />
            <Route path="/registerPS" element={<Register />} />
            <Route path="/registerSP" element={<RegisterServiceProvider />} />
            <Route path="/registerPO" element={<RegisisterPropertyOwner />} />
            <Route path="/addService" element={<AddServiceInfo />} />
            <Route path="/experienceLevel" element={<ExperienceLevel />} />

            {/* Email Verification & Onboarding Routes */}
            <Route path='/verify-email/:id' element={<UserEmailVerification />} />
            <Route path='/onboarding-intent' element={<OnboardingIntent />} />

            {/* User Protected Routes */}
            <Route path='/user/bookings' element={<UserProtectedRoute><Home /><Bookings /></UserProtectedRoute>} />
            <Route path='/accountbilling/*' element={<UserProtectedRoute><AccountManagement /></UserProtectedRoute>} />
            <Route path='/accountbilling' element={<UserProtectedRoute><AccountBillingView /></UserProtectedRoute>} />
            <Route path='/update-account' element={<UserProtectedRoute><UpdateAccountView /></UserProtectedRoute>} />
            <Route path='/help' element={<UserProtectedRoute><HelpView /></UserProtectedRoute>} />
            <Route path='/vacationPlan' element={<UserProtectedRoute><ComingSoon /></UserProtectedRoute>} />
            <Route path='/serviceProvider/:offerId' element={<UserProtectedRoute><SingleServiceView /></UserProtectedRoute>} />
            <Route path='/singleprovider/:offerId' element={<SingleServiceView />} />
            <Route path="/propertyDetails/:id" element={<><Home /><PropertyDetails /></>} />
            <Route path='/hotel/:id' element={<><Home /><HotelDetailsPage /></>} />

            {/* Role dashboards */}
            <Route path='/dashboard/property/*' element={<Suspense fallback={<Loader />}><PropertyOwnerRoutes /></Suspense>} />
            <Route path='/dashboard/provider/*' element={<Suspense fallback={<Loader />}><ServiceProviderRoutes /></Suspense>} />
            <Route path='/dashboard/hotel/*' element={<Suspense fallback={<Loader />}><HotelRoutes /></Suspense>} />


          </Routes>
        </AutoLogoutWrapper>
      </BrowserRouter>
    </div>
  );
}

export default App;
